/**
 * Live — Screen 2
 * Call cards, up-next queue, activity log, transfer-ready state.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchLiveState } from '../../lib/apiHelpers.js';
import { AiOrb } from '../../components/AiOrb.js';
import { Waveform } from '../../components/Waveform.js';
import { AvatarInitials } from '../../components/AvatarInitials.js';
import { ComingSoon } from '../../components/ComingSoon.js';

function fmtDuration(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

const SENTIMENT_CONFIG = {
  positive: { label: 'Positive', color: '#159662', bg: 'rgba(21,150,98,0.1)' },
  neutral: { label: 'Neutral', color: '#64748B', bg: 'rgba(100,116,139,0.1)' },
  tense: { label: 'Tense', color: '#EF4444', bg: 'rgba(239,68,68,0.1)' },
};

// Demo data
const DEMO_LIVE_CALLS = [
  {
    leadId: 'ld_02',
    leadName: 'Denise Okafor',
    initials: 'DO',
    state: 'FL',
    attemptNo: 1,
    phase: 'Live transfer',
    sentiment: 'positive' as const,
    durationS: 84,
    transcript: [
      { who: 'Ava', text: '…you both qualify for the simplified options. I have Dan available right now, want me to bring him on?' },
      { who: 'Denise', text: 'Yes, please.' },
    ],
    transferReady: true,
  },
  {
    leadId: 'ld_21',
    leadName: 'Hank Willis',
    initials: 'HW',
    state: 'TX',
    attemptNo: 2,
    phase: 'Qualifying',
    sentiment: 'neutral' as const,
    durationS: 134,
    transcript: [
      { who: 'Hank', text: 'I\'ve got a $180k mortgage in Cedar Park.' },
      { who: 'Ava', text: 'Perfect — and is that just in your name, or joint with Carla?' },
    ],
    transferReady: false,
  },
  {
    leadId: 'ld_22',
    leadName: 'Rosa Jimenez',
    initials: 'RJ',
    state: 'TX',
    attemptNo: 1,
    phase: 'Opening',
    sentiment: 'neutral' as const,
    durationS: 23,
    transcript: [
      { who: 'Rosa', text: 'Hello?' },
      { who: 'Ava', text: 'Hi Rosa! This is Ava with Mortgage Protection Quotes — you requested info about coverage for your home.' },
    ],
    transferReady: false,
  },
];

const DEMO_QUEUE = [
  { id: 'q1', time: '11:38 AM', lead: 'Gloria Sanchez', reason: 'Attempt 1 — no answer 20m ago' },
  { id: 'q2', time: '11:41 AM', lead: 'Pete Duncan', reason: 'Attempt 1 — just arrived' },
  { id: 'q3', time: '11:45 AM', lead: 'Marcy Cole', reason: 'Attempt 2 — redial window' },
  { id: 'q4', time: '11:52 AM', lead: 'Linda Osei', reason: 'Attempt 3 — final try' },
];

const DEMO_ACTIVITY = [
  { id: 'a1', time: '11:31 AM', text: 'Ava connected with Denise Okafor — transfer ready' },
  { id: 'a2', time: '11:29 AM', text: 'Ava dialed Rosa Jimenez (attempt 1)' },
  { id: 'a3', time: '11:22 AM', text: 'Ava left voicemail for Patrick Schultz' },
  { id: 'a4', time: '10:56 AM', text: 'James Thornton enrolled in Fresh Lead sequence' },
  { id: 'a5', time: '10:05 AM', text: 'Ava left voicemail for Linda Osei' },
];

// ── Live Call Card ────────────────────────────────────────────────────────────

function LiveCallCard({ call }: { call: typeof DEMO_LIVE_CALLS[0] }) {
  const sentiment = SENTIMENT_CONFIG[call.sentiment];

  return (
    <div
      className="bg-surface rounded-card p-4 flex flex-col gap-3"
      style={{
        boxShadow: call.transferReady
          ? '0 0 0 2px #159662, 0 0 0 4px rgba(21,150,98,0.12)'
          : '0 1px 4px rgba(13,23,41,0.05)',
        border: call.transferReady ? '1px solid #159662' : '1px solid transparent',
      }}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <AvatarInitials name={call.leadName} size={40} ring="green" badge />
        <div className="flex-1 min-w-0">
          <p
            className="text-[14px] font-semibold text-ink"
            style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
          >
            {call.leadName}
          </p>
          <p className="text-[11.5px] text-muted">
            {call.state} · Attempt {call.attemptNo}
          </p>
        </div>
        {/* Duration */}
        <span
          className="text-[14px] font-medium text-ink"
          style={{ fontFamily: '"DM Mono", monospace' }}
        >
          {fmtDuration(call.durationS)}
        </span>
      </div>

      {/* Transcript rolling */}
      <div className="flex flex-col gap-1 px-2 py-2 rounded-[8px]" style={{ background: '#FAFBFC' }}>
        {call.transcript.map((line, i) => (
          <p
            key={i}
            className={i === call.transcript.length - 1 ? 'text-[12px] text-ink' : 'text-[11px] text-muted'}
          >
            <span className="font-semibold">{line.who}</span>{' '}
            &ldquo;{line.text}&rdquo;
          </p>
        ))}
      </div>

      {/* Waveform */}
      <div className="flex items-center gap-2">
        <Waveform color={call.transferReady ? '#159662' : '#6D4AEA'} height={14} />
        <span className="text-[10px] text-muted flex-1">Live call in progress</span>
      </div>

      {/* Footer chips + actions */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Phase chip */}
        <span
          className="px-2 py-0.5 rounded-chip text-[10px] font-semibold"
          style={
            call.transferReady
              ? { background: 'rgba(21,150,98,0.1)', color: '#159662', border: '1px solid rgba(21,150,98,0.25)' }
              : { background: 'rgba(109,74,234,0.1)', color: '#6D4AEA', border: '1px solid rgba(109,74,234,0.25)' }
          }
        >
          {call.transferReady ? 'Transfer ready — closer requested' : call.phase}
        </span>

        {/* Sentiment chip */}
        <span
          className="px-2 py-0.5 rounded-chip text-[10px] font-semibold"
          style={{ background: sentiment.bg, color: sentiment.color }}
        >
          {sentiment.label}
        </span>

        <div className="flex-1" />

        {/* Action buttons */}
        <button className="text-[11px] font-medium text-muted hover:text-ink transition-colors px-2 py-1">
          Listen in
        </button>

        {call.transferReady ? (
          <button
            className="px-3 py-1.5 rounded-btn text-[11.5px] font-semibold text-white transition-colors"
            style={{ background: '#159662' }}
          >
            Accept transfer
          </button>
        ) : (
          <button
            className="px-3 py-1.5 rounded-btn text-[11.5px] font-semibold text-white transition-colors"
            style={{ background: '#0D1729' }}
          >
            Take the call
          </button>
        )}

        <button
          className="px-3 py-1.5 rounded-btn text-[11.5px] font-medium transition-colors"
          style={{ background: 'rgba(46,102,224,0.08)', color: '#2E66E0' }}
        >
          Open thread
        </button>
      </div>
    </div>
  );
}

// ── Queue Card ────────────────────────────────────────────────────────────────

function QueueCard() {
  const [paused, setPaused] = useState(false);

  return (
    <div className="bg-surface rounded-card shadow-card">
      <div
        className="px-4 py-3 flex items-center justify-between"
        style={{ borderBottom: '1px solid #F0F3F8' }}
      >
        <h3
          className="text-[13.5px] font-semibold text-ink"
          style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
        >
          Up next
        </h3>
        <button
          onClick={() => setPaused((p) => !p)}
          className="px-2.5 py-1 rounded-[8px] text-[10.5px] font-semibold transition-colors"
          style={
            paused
              ? { background: 'rgba(21,150,98,0.1)', color: '#159662' }
              : { background: 'rgba(251,191,36,0.12)', color: '#B45309' }
          }
        >
          {paused ? 'Resume queue' : 'Pause queue'}
        </button>
      </div>

      {paused && (
        <div
          className="mx-3 my-2 px-3 py-2 rounded-[8px] text-[11px]"
          style={{ background: 'rgba(251,191,36,0.08)', color: '#B45309', border: '1px solid rgba(251,191,36,0.2)' }}
        >
          Outbound paused — Ava stops new calls. Inbound replies still answered, live calls finish normally.
        </div>
      )}

      <div className={`divide-y divide-hairline ${paused ? 'opacity-40' : ''}`}>
        {DEMO_QUEUE.map((item) => (
          <div key={item.id} className="px-4 py-3 flex items-center gap-3">
            <span
              className="text-[11px] text-muted flex-shrink-0"
              style={{ fontFamily: '"DM Mono", monospace' }}
            >
              {item.time}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[12.5px] font-semibold text-ink truncate">{item.lead}</p>
              <p className="text-[11px] text-muted truncate">{item.reason}</p>
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted flex-shrink-0">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Activity Log ─────────────────────────────────────────────────────────────

function ActivityLogCard() {
  return (
    <div className="bg-surface rounded-card shadow-card">
      <div
        className="px-4 py-3"
        style={{ borderBottom: '1px solid #F0F3F8' }}
      >
        <h3
          className="text-[13.5px] font-semibold text-ink"
          style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
        >
          Activity log
        </h3>
      </div>
      <div className="divide-y divide-hairline max-h-64 overflow-y-auto">
        {DEMO_ACTIVITY.map((item) => (
          <div key={item.id} className="px-4 py-2.5 flex items-start gap-2">
            <span
              className="text-[10.5px] text-muted flex-shrink-0 mt-0.5"
              style={{ fontFamily: '"DM Mono", monospace', width: 60 }}
            >
              {item.time}
            </span>
            <p className="text-[12px] text-ink">{item.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────

interface LivePageProps {
  aiName: string;
}

export function LivePage({ aiName }: LivePageProps) {
  const sortedCalls = [...DEMO_LIVE_CALLS].sort((a, b) =>
    a.transferReady === b.transferReady ? 0 : a.transferReady ? -1 : 1,
  );

  const liveCount = sortedCalls.length;
  const queueCount = DEMO_QUEUE.length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1
              className="text-[22px] font-bold text-ink tracking-[-0.01em]"
              style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
            >
              Live
            </h1>
            {/* Pulsing green dot */}
            <div className="relative w-2.5 h-2.5 flex-shrink-0">
              <div className="w-full h-full rounded-full bg-green-DEFAULT" />
              <div
                className="absolute inset-0 rounded-full bg-green-DEFAULT"
                style={{ animation: 'cr-pulse 1.6s ease-out infinite' }}
              />
            </div>
          </div>
          <p className="text-[13.5px] text-muted">
            {liveCount} calls ·{' '}
            <ComingSoon inline label="2 hot text exchanges — coming soon" />{' '}
            · {queueCount} dials queued · {aiName} runs them all in parallel
          </p>
        </div>

        {/* Quiet hours notice */}
        <div
          className="px-3 py-1.5 rounded-chip text-[11px] font-medium flex-shrink-0"
          style={{ background: 'rgba(251,191,36,0.1)', color: '#B45309', border: '1px solid rgba(251,191,36,0.2)' }}
        >
          Quiet hours enforced · next check 6:15 PM
        </div>
      </div>

      {/* Layout: 1.55fr left / 1fr right */}
      <div className="grid gap-5" style={{ gridTemplateColumns: '1.55fr 1fr' }}>
        {/* Left: call cards + greyed text exchanges */}
        <div className="flex flex-col gap-4">
          {sortedCalls.map((call) => (
            <LiveCallCard key={call.leadId} call={call} />
          ))}

          {/* Greyed hot text exchanges */}
          <ComingSoon label="Text channel coming soon">
            <div>
              <div
                className="flex items-center gap-2 mb-3"
                style={{ borderTop: '1px solid #F0F3F8', paddingTop: 16 }}
              >
                <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted">
                  Hot text exchanges
                </span>
                <div className="flex-1 h-px bg-hairline" />
              </div>
              <div className="bg-surface rounded-card p-4 flex items-center gap-3 opacity-60">
                <AvatarInitials name="Sample Lead" size={36} ring="green" />
                <div>
                  <p className="text-[12.5px] font-semibold text-ink">Sample Lead</p>
                  <p className="text-[11px] text-muted">6 messages in 4 min · last 20s ago</p>
                </div>
              </div>
            </div>
          </ComingSoon>

          {/* Empty state if 0 live */}
          {liveCount === 0 && (
            <div
              className="border-2 border-dashed rounded-card p-8 flex flex-col items-center gap-3 text-center"
              style={{ borderColor: '#E5EAF2' }}
            >
              <AiOrb size={40} pulse={false} />
              <div>
                <p className="text-[14px] font-semibold text-ink">{aiName} is between calls</p>
                <p className="text-[12.5px] text-muted">Next dial in 2:40</p>
              </div>
            </div>
          )}
        </div>

        {/* Right: queue + activity */}
        <div className="flex flex-col gap-4">
          <QueueCard />
          <ActivityLogCard />
        </div>
      </div>
    </div>
  );
}
